import React, { useState } from "react";
import {
  Box,
  Chip,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@material-ui/core";
import { ExpandLess, ExpandMore, FilterList, Search } from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";
import { DatePicker, LocalizationProvider } from "@material-ui/lab";
import AdapterDateFns from "@material-ui/lab/AdapterDateFns";
import { es } from "date-fns/locale";
import {
  ESTADOS_TAREA_CLIENTE,
  ESTADOS_FACTURA_CLIENTE,
  TIPOS_TAREA,
} from "../hooks/useClienteFilters";
import {
  panelStyles,
  filterChipStyles,
  searchInputStyles,
  UI_COLORS,
} from "../styles/clientePortalStyles";
import ActiveFiltersChips from "../../tareas/filters/ActiveFiltersChips";

// ==================== ESTILOS ====================

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  header: {
    ...panelStyles.header,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerExpanded: {
    borderRadius: "12px 12px 0 0",
    borderBottom: "none",
  },
  headerTitle: {
    fontWeight: 600,
    color: UI_COLORS.text.primary,
  },
  filterCount: {
    backgroundColor: "#1976D2",
    color: "#FFFFFF",
    fontWeight: 500,
    fontSize: "0.75rem",
  },
  content: {
    ...panelStyles.content,
  },
  sectionLabel: {
    fontSize: "0.75rem",
    color: UI_COLORS.text.secondary,
    fontWeight: 500,
    marginBottom: theme.spacing(0.5),
    display: "block",
  },
  estadoChip: {
    ...filterChipStyles,
    cursor: "pointer",
  },
  searchField: {
    ...searchInputStyles,
  },
  selectField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },
  dateField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },
}));

/**
 * Componente ClienteFilterPanel - Panel de filtros para portal de clientes
 *
 * @param {string} tipo - Tipo de filtro: "tareas" | "facturas"
 * @param {object} filtros - Estado actual de filtros
 * @param {function} updateFiltro - Función para actualizar un filtro
 * @param {function} toggleEstado - Función para toggle de estados
 * @param {function} limpiarFiltros - Función para limpiar todos los filtros
 * @param {function} removerFiltro - Función para remover un filtro específico
 * @param {number} filtrosActivos - Cantidad de filtros activos
 * @param {boolean} hayFiltrosActivos - Si hay filtros activos
 * @param {string} searchPlaceholder - Placeholder del campo de búsqueda (opcional)
 *
 * @example Para tareas
 * <ClienteFilterPanel
 *   tipo="tareas"
 *   filtros={filtros}
 *   updateFiltro={updateFiltro}
 *   toggleEstado={toggleEstado}
 *   limpiarFiltros={limpiarFiltros}
 *   removerFiltro={removerFiltro}
 *   filtrosActivos={filtrosActivos}
 *   hayFiltrosActivos={hayFiltrosActivos}
 *   searchPlaceholder="Buscar tarea..."
 * />
 *
 * @example Para facturas
 * <ClienteFilterPanel
 *   tipo="facturas"
 *   filtros={filtros}
 *   updateFiltro={updateFiltro}
 *   toggleEstado={toggleEstado}
 *   limpiarFiltros={limpiarFiltros}
 *   removerFiltro={removerFiltro}
 *   filtrosActivos={filtrosActivos}
 *   hayFiltrosActivos={hayFiltrosActivos}
 *   searchPlaceholder="Buscar factura..."
 * />
 */
export default function ClienteFilterPanel({
  tipo = "tareas",
  filtros,
  updateFiltro,
  toggleEstado,
  limpiarFiltros,
  removerFiltro,
  filtrosActivos,
  hayFiltrosActivos,
  searchPlaceholder = "Buscar...",
}) {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(true);

  // Determinar qué estados mostrar según el tipo
  const estadosDisponibles =
    tipo === "facturas" ? ESTADOS_FACTURA_CLIENTE : ESTADOS_TAREA_CLIENTE;

  const handleToggleExpand = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={es}>
      <Box className={classes.container}>
        {/* Header del panel */}
        <Box
          className={`${classes.header} ${
            expanded ? classes.headerExpanded : ""
          }`}
          onClick={handleToggleExpand}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <FilterList
              sx={{ color: UI_COLORS.text.secondary, fontSize: 20 }}
            />
            <Typography variant="subtitle1" className={classes.headerTitle}>
              Filtros
            </Typography>
            {filtrosActivos > 0 && (
              <Chip
                size="small"
                label={`${filtrosActivos} activo${
                  filtrosActivos > 1 ? "s" : ""
                }`}
                className={classes.filterCount}
              />
            )}
          </Stack>
          <IconButton
            size="small"
            aria-label={expanded ? "Ocultar filtros" : "Mostrar filtros"}
            onClick={(event) => {
              event.stopPropagation();
              handleToggleExpand();
            }}
          >
            {expanded ? (
              <ExpandLess sx={{ color: UI_COLORS.text.secondary }} />
            ) : (
              <ExpandMore sx={{ color: UI_COLORS.text.secondary }} />
            )}
          </IconButton>
        </Box>

        {/* Contenido colapsable */}
        <Collapse in={expanded}>
          <Box className={classes.content}>
            <Grid container spacing={2.5}>
              {/* Fila 1: Búsqueda y Estados */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Buscar"
                  placeholder={searchPlaceholder}
                  value={filtros.busqueda}
                  onChange={(e) => updateFiltro("busqueda", e.target.value)}
                  className={classes.searchField}
                  InputProps={{
                    startAdornment: (
                      <Search
                        sx={{
                          mr: 0.5,
                          color: UI_COLORS.text.disabled,
                          fontSize: 20,
                        }}
                      />
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={8}>
                <Box>
                  <Typography className={classes.sectionLabel}>
                    Estado
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={0.75}>
                    {estadosDisponibles.map((estado) => {
                      const isSelected = filtros.estados?.includes(
                        estado.value,
                      );
                      return (
                        <Chip
                          key={estado.value}
                          label={estado.label}
                          size="small"
                          color={isSelected ? estado.color : "default"}
                          variant={isSelected ? "filled" : "outlined"}
                          onClick={() => toggleEstado(estado.value)}
                          className={classes.estadoChip}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              </Grid>

              {/* Fila 2: Filtros específicos según tipo */}
              {tipo === "tareas" && (
                <>
                  {/* Tipo de Tarea */}
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl
                      fullWidth
                      size="small"
                      className={classes.selectField}
                    >
                      <InputLabel>Tipo de Tarea</InputLabel>
                      <Select
                        value={filtros.tipoTarea}
                        label="Tipo de Tarea"
                        onChange={(e) =>
                          updateFiltro("tipoTarea", e.target.value)
                        }
                      >
                        <MenuItem value="">
                          <em>Todos</em>
                        </MenuItem>
                        {TIPOS_TAREA.map((tipo) => (
                          <MenuItem key={tipo.value} value={tipo.value}>
                            {tipo.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Rango de fechas de inicio */}
                  <Grid item xs={12} sm={6} md={4}>
                    <DatePicker
                      label="Inicio desde"
                      value={filtros.fechaInicioDesde}
                      onChange={(newValue) =>
                        updateFiltro("fechaInicioDesde", newValue)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          size="small"
                          className={classes.dateField}
                        />
                      )}
                      clearable
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <DatePicker
                      label="Inicio hasta"
                      value={filtros.fechaInicioHasta}
                      onChange={(newValue) =>
                        updateFiltro("fechaInicioHasta", newValue)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          size="small"
                          className={classes.dateField}
                        />
                      )}
                      minDate={filtros.fechaInicioDesde || undefined}
                      clearable
                    />
                  </Grid>
                </>
              )}

              {tipo === "facturas" && (
                <>
                  {/* Rango de fechas de facturación */}
                  <Grid item xs={12} sm={6} md={3}>
                    <DatePicker
                      label="Facturación desde"
                      value={filtros.fechaFacturacionDesde}
                      onChange={(newValue) =>
                        updateFiltro("fechaFacturacionDesde", newValue)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          size="small"
                          className={classes.dateField}
                        />
                      )}
                      clearable
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <DatePicker
                      label="Facturación hasta"
                      value={filtros.fechaFacturacionHasta}
                      onChange={(newValue) =>
                        updateFiltro("fechaFacturacionHasta", newValue)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          size="small"
                          className={classes.dateField}
                        />
                      )}
                      minDate={filtros.fechaFacturacionDesde || undefined}
                      clearable
                    />
                  </Grid>

                  {/* Rango de montos (opcional) */}
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Monto mínimo"
                      type="number"
                      value={filtros.montoMin}
                      onChange={(e) => updateFiltro("montoMin", e.target.value)}
                      className={classes.searchField}
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ mr: 0.5 }}>$</Typography>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Monto máximo"
                      type="number"
                      value={filtros.montoMax}
                      onChange={(e) => updateFiltro("montoMax", e.target.value)}
                      className={classes.searchField}
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ mr: 0.5 }}>$</Typography>
                        ),
                      }}
                    />
                  </Grid>
                </>
              )}
            </Grid>

            {/* Chips de filtros activos */}
            <ActiveFiltersChips
              filtros={filtros}
              removerFiltro={removerFiltro}
              limpiarFiltros={limpiarFiltros}
              hayFiltrosActivos={hayFiltrosActivos}
              estadosDisponibles={estadosDisponibles}
            />
          </Box>
        </Collapse>
      </Box>
    </LocalizationProvider>
  );
}
